import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { fetchActivityMatrixNy } from "../../../api/nystats";
import { ProfileIcon, LikeIcon, ReportIcon, BlockIcon, PaymentIcon, DurationIcon, IdleIcon } from "../../../icons";

export default function ActivityMatrix() {
     const filter = useSelector((state) => state.filter);
    const filterWithoutUTM = useMemo(() => {
      const { utm_options, ...rest } = filter;
      return rest;
    }, [JSON.stringify(filter)]);

  const [stats, setStats] = useState({
    viewedProfiles: 0,
    sentLikes: 0,
    reportedUsers: 0,
    blockedUsers: 0,
    paymentAttempts: 0,
    paymentSuccess: 0,
    avgSessionDuration: "0 min",
    idleAccounts: 0,
  });

  const [isLoading, setIsLoading] = useState(true);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const data = await fetchActivityMatrixNy(filter);
      // data is the formattedData from backend
      setStats({
        viewedProfiles: data.viewedProfiles || 0,
        sentLikes: data.sentLikes || 0,
        reportedUsers: data.reportedUsers || 0,
        blockedUsers: data.blockedUsers || 0,
        paymentAttempts: data.paymentAttempts || 0,
        paymentSuccess: data.paymentSuccess || 0,
        avgSessionDuration: data.avgSessionDuration || "0 min",
        idleAccounts: data.idleAccounts || 0,
      });
    } catch (err) {
      console.error("Error loading activity matrix", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [JSON.stringify(filterWithoutUTM)]);

  const formatNumber = (n) =>
    typeof n === "number" ? n.toLocaleString() : n;

  const items = [
    {
      icon: <ProfileIcon />,
      label: "Viewed Profiles",
      value: formatNumber(stats.viewedProfiles),
    },
    {
      icon: <LikeIcon />,
      label: "Sent Likes",
      value: formatNumber(stats.sentLikes),
    },
    {
      icon: <ReportIcon />,
      label: "Reported/Flagged Users",
      value: formatNumber(stats.reportedUsers),
    },
    {
      icon: <BlockIcon />,
      label: "Blocked Users",
      value: formatNumber(stats.blockedUsers),
    },
    {
      icon: <PaymentIcon />,
      label: "Payment Attempts",
      value: formatNumber(stats.paymentAttempts),
    },
    {
      icon: <PaymentIcon />,
      label: "Payment Success",
      value: formatNumber(stats.paymentSuccess),
    },
    {
      icon: <DurationIcon />,
      label: "Avg Session Duration",
      value: stats.avgSessionDuration, // already a string like "45 min"
    },
    {
      icon: <IdleIcon />,
      label: "Idle Accounts (30+ Days)",
      value: formatNumber(stats.idleAccounts),
    },
  ];

  return (
    <Box
      sx={{
        border: "1px solid #e6e6e6",
        borderRadius: "12px",
        p: 2,
        background: "#fff",
      }}
    >
      <Typography
        sx={{
          fontSize: "18px",
          color: "#101828",
          mb: 2,
          borderLeft: "3px solid #FBBF24",
          pl: 1,
          fontWeight: 500,
        }}
      >
        Activity Matrix (All Possible Actions)
      </Typography>

      {isLoading && (
       <Box sx={{ textAlign: 'center', py: 4 }}>
				  <Typography variant="body1" color="text.secondary">
					  Loading...
				  </Typography>
			  </Box>
      )}

      {!isLoading && <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 1.5,
        }}
      >
        {items.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
              px: 1.5,
              border: "1px solid #e6e6e6",
              borderRadius: "8px",
            }}
          >
            {/* Left: icon + label */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {item.icon}
              <Typography sx={{ color: "#6A7282", fontSize: "14px" }}>
                {item.label}
              </Typography>
            </Box>

            {/* Right: value */}
            <Typography
              sx={{
                color: "#101828",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>}
    </Box>
  );
}
